import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import { reportService, IdleTimeActivityReportEntry } from '../services/reportService';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  name: string;
  email: string;
}

const IdleTimeActivityReport: React.FC = () => {
  const { user } = useAuth();

  const canViewReport = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => ['pmp_admin', 'site_admin'].includes(role.slug));
  };

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  const formatDate = (d: Date) => d.toISOString().split('T')[0];

  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<string>(formatDate(sevenDaysAgo));
  const [endDate, setEndDate] = useState<string>(formatDate(today));

  const [users, setUsers] = useState<User[]>([]);
  const [reportData, setReportData] = useState<IdleTimeActivityReportEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  useEffect(() => {
    if (canViewReport()) loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch {
      setError('Failed to load users');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params: { start_date: string; end_date: string; user_id?: number } = {
        start_date: startDate,
        end_date: endDate,
      };
      if (selectedUserId) params.user_id = selectedUserId;
      const data = await reportService.getIdleTimeActivityReport(params);
      setReportData(data);
      setPage(0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) return;

    const headers = [
      'User',
      'Date',
      'Nudges Shown',
      'Nudges Continued',
      'Nudges Paused Timer',
      'Timer Sessions',
      'First Timer Start',
      'Last Timer End',
      'Total Tracked',
      'Gap Count',
      'Total Gap Time',
      'Gaps Detail',
    ];

    const rows = reportData.map(row => [
      row.user_name,
      row.date,
      row.nudge_shown_count,
      row.nudge_continue_count,
      row.nudge_pause_count,
      row.timer_session_count,
      row.first_timer_start ?? '',
      row.last_timer_end ?? '',
      row.total_timer_formatted,
      row.gap_count,
      row.total_gap_formatted,
      row.gaps.map(g => `${g.from}-${g.to} (${g.formatted})`).join('; '),
    ]);

    const csvContent = [headers, ...rows]
      .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `idle-timer-summary_${startDate}_to_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const paginatedData = reportData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!canViewReport()) {
    return (
      <Box>
        <Alert severity="error">
          Only PMP Admin and Site Admin can access the Idle Timer Summary report.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Idle Timer Summary Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One row per user per day — idle timer notifications received and gaps between active timer sessions.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'flex-start' } }}>
          <Box sx={{ flex: { xs: 1, md: 2 }, minWidth: { xs: '100%', md: '200px' } }}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUserId}
                label="User"
                onChange={(e) => setSelectedUserId(e.target.value as number | '')}
              >
                <MenuItem value="">All users</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '150px' } }}>
            <TextField
              fullWidth label="Start date" type="date" value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '150px' } }}>
            <TextField
              fullWidth label="End date" type="date" value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
            <Button
              fullWidth variant="contained" color="primary"
              onClick={handleSearch} disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ height: '56px' }}
            >
              Search
            </Button>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '140px' } }}>
            <Button
              fullWidth variant="outlined" color="primary"
              onClick={handleExportCSV}
              disabled={loading || reportData.length === 0}
              startIcon={<DownloadIcon />}
              sx={{ height: '56px' }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Results table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            Results
            {reportData.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({reportData.length} user-day{reportData.length !== 1 ? 's' : ''})
              </Typography>
            )}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                <TableCell>User</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">
                  <Tooltip title="Number of times the idle timer notification was displayed">
                    <span>Nudges Shown</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="User clicked Continue on the nudge">
                    <span>Continued</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="User paused/stopped the timer from the nudge">
                    <span>Paused Timer</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">Timer Sessions</TableCell>
                <TableCell>Active Window</TableCell>
                <TableCell>Total Tracked</TableCell>
                <TableCell align="center">Gaps</TableCell>
                <TableCell>Total Gap Time</TableCell>
                <TableCell>Gap Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No data. Set filters and click Search.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, idx) => (
                  <TableRow key={`${row.user_id}-${row.date}-${idx}`} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.user_name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>

                    {/* Nudge shown — highlight if > 0 */}
                    <TableCell align="center">
                      {row.nudge_shown_count > 0 ? (
                        <Chip
                          label={row.nudge_shown_count}
                          size="small"
                          color={row.nudge_shown_count >= 5 ? 'error' : row.nudge_shown_count >= 3 ? 'warning' : 'default'}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {row.nudge_continue_count > 0 ? row.nudge_continue_count : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </TableCell>

                    <TableCell align="center">
                      {row.nudge_pause_count > 0 ? (
                        <Chip label={row.nudge_pause_count} size="small" color="info" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {row.timer_session_count > 0 ? row.timer_session_count : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </TableCell>

                    {/* Active window: first start → last end */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.first_timer_start && row.last_timer_end
                        ? `${row.first_timer_start} → ${row.last_timer_end}`
                        : <Typography variant="body2" color="text.secondary">—</Typography>
                      }
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.total_timer_minutes > 0
                        ? row.total_timer_formatted
                        : <Typography variant="body2" color="text.secondary">—</Typography>
                      }
                    </TableCell>

                    {/* Gap count */}
                    <TableCell align="center">
                      {row.gap_count > 0 ? (
                        <Chip
                          label={row.gap_count}
                          size="small"
                          color={row.gap_count >= 4 ? 'error' : row.gap_count >= 2 ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.total_gap_minutes > 0
                        ? <Typography variant="body2" color={row.total_gap_minutes >= 60 ? 'error' : 'text.primary'}>{row.total_gap_formatted}</Typography>
                        : <Typography variant="body2" color="text.secondary">—</Typography>
                      }
                    </TableCell>

                    {/* Gap detail */}
                    <TableCell>
                      {row.gaps.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {row.gaps.map((g, i) => (
                            <Tooltip key={i} title={`${g.from} → ${g.to} (${g.formatted})`}>
                              <Chip
                                label={`${g.from}–${g.to}`}
                                size="small"
                                variant="outlined"
                                color={g.minutes >= 60 ? 'error' : g.minutes >= 30 ? 'warning' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {reportData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, reportData.length)} of {reportData.length}
            </Typography>
            <TablePagination
              component="div"
              count={reportData.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 30, 50, 100]}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default IdleTimeActivityReport;
